import { axiosInstance } from "./axios";
import { isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import { identifyCurrentUser } from "./analytics";

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatorEmail: string;
  targetEmail: string;
}

interface OriginalTokens {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  profile: string;
  claims: string;
}

const IMPERSONATION_STATE_KEY = "impersonation_state";
const ORIGINAL_TOKENS_KEY = "original_tokens";

export const IMPERSONATION_BANNER_HEIGHT_PX = 44;

type AccessTokenPayload = {
  mkClaims?: { superKudu?: boolean };
  hgClaims?: { superKudu?: boolean };
};

function extractServerMessage(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === "string") return data;

  if (typeof data === "object") {
    const maybeMessage = (data as { message?: unknown }).message;
    if (typeof maybeMessage === "string") return maybeMessage;
    if (Array.isArray(maybeMessage)) {
      const parts = maybeMessage.filter((item) => typeof item === "string");
      if (parts.length > 0) return parts.join("\n");
    }

    const maybeDescription = (data as { error_description?: unknown })
      .error_description;
    if (typeof maybeDescription === "string") return maybeDescription;
  }

  return null;
}

function getImpersonationErrorMessage(
  error: unknown,
  targetEmail: string
): string {
  if (!isAxiosError(error)) {
    return "Failed to impersonate user. Please try again.";
  }

  const status = error.response?.status;
  const serverMessage = extractServerMessage(error.response?.data);

  if (
    status === 400 &&
    serverMessage?.toLowerCase().includes("target user not found")
  ) {
    return `No user exists for "${targetEmail}". Please check the email address and try again.`;
  }

  if (status === 401 || status === 403) {
    return "You are not authorized to impersonate users.";
  }

  if (serverMessage) return serverMessage;

  return "Failed to impersonate user. Please check the email address and try again.";
}

/**
 * Check if the current user can see the impersonation option.
 * Only superKudu users can see the option.
 * Actual authorization is enforced by mk-auth (superKudu) on the backend.
 */
export function canImpersonate(): boolean {
  const rawClaims = localStorage.getItem("claims");
  if (!rawClaims) return false;
  try {
    const parsed = JSON.parse(rawClaims);
    return parsed?.superKudu === true;
  } catch {
    return false;
  }
}

/**
 * Get the current impersonation state from localStorage
 */
export function getImpersonationState(): ImpersonationState | null {
  const state = localStorage.getItem(IMPERSONATION_STATE_KEY);
  if (!state) return null;
  try {
    return JSON.parse(state) as ImpersonationState;
  } catch {
    return null;
  }
}

/**
 * Check if the user is currently impersonating another user
 */
export function isImpersonating(): boolean {
  const state = getImpersonationState();
  return state?.isImpersonating ?? false;
}

/**
 * Get the email of the user being impersonated
 */
export function getImpersonatedEmail(): string | null {
  const state = getImpersonationState();
  return state?.targetEmail ?? null;
}

/**
 * Get the email of the impersonator (the original user)
 */
export function getImpersonatorEmail(): string | null {
  const state = getImpersonationState();
  return state?.impersonatorEmail ?? null;
}

/**
 * Clear impersonation artifacts without restoring tokens.
 * This is used on logout / fresh login to avoid stale restoration.
 */
export function clearImpersonationState(): void {
  localStorage.removeItem(IMPERSONATION_STATE_KEY);
  localStorage.removeItem(ORIGINAL_TOKENS_KEY);
}

/**
 * Best-effort check for whether the impersonator (original session) is superKudu.
 * This is UX-only; authorization is enforced server-side.
 */
export function isImpersonatorSuperKudu(): boolean {
  const originalTokensStr = localStorage.getItem(ORIGINAL_TOKENS_KEY);
  if (!originalTokensStr) return false;

  try {
    const originalTokens: OriginalTokens = JSON.parse(originalTokensStr);

    // Prefer decoding the original access token (more direct).
    if (originalTokens.access_token) {
      const payload = jwtDecode<AccessTokenPayload>(
        originalTokens.access_token
      );
      if (payload?.mkClaims?.superKudu === true) return true;
      if (payload?.hgClaims?.superKudu === true) return true;
    }

    // Fallback to stored claims (server-provided during auth).
    if (originalTokens.claims) {
      const claims = JSON.parse(originalTokens.claims) as {
        superKudu?: unknown;
      };
      return claims?.superKudu === true;
    }
  } catch {
    return false;
  }

  return false;
}

/**
 * Start impersonation session.
 * Saves original tokens, calls the impersonate endpoint, and applies new tokens.
 */
export async function startImpersonation(targetEmail: string): Promise<void> {
  const trimmedTargetEmail = targetEmail.trim();

  // If already impersonating, restore original session first so we can
  // safely switch to a new target without losing original tokens.
  if (isImpersonating()) {
    endImpersonation();
  }

  // Save original tokens
  const originalTokens: OriginalTokens = {
    access_token: localStorage.getItem("access_token") || "",
    refresh_token: localStorage.getItem("refresh_token") || "",
    expires_at: localStorage.getItem("expires_at") || "",
    profile: localStorage.getItem("profile") || "",
    claims: localStorage.getItem("claims") || "",
  };

  localStorage.setItem(ORIGINAL_TOKENS_KEY, JSON.stringify(originalTokens));

  // Get the impersonator's email before we change tokens
  const profile = localStorage.getItem("profile");
  let impersonatorEmail = "";
  if (profile) {
    try {
      impersonatorEmail = JSON.parse(profile).email || "";
    } catch {
      impersonatorEmail = "";
    }
  }

  try {
    // Call the impersonate endpoint
    const response = await axiosInstance.post("/auth/impersonate", {
      targetEmail: trimmedTargetEmail,
    });

    const {
      accessToken,
      refreshToken,
      expiresAt,
      profile: newProfile,
      claims,
      impersonating,
    } = response.data;

    // Apply new tokens
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    localStorage.setItem("expires_at", JSON.stringify(expiresAt));
    localStorage.setItem("profile", JSON.stringify(newProfile));
    localStorage.setItem("claims", JSON.stringify(claims));

    // Save impersonation state
    const impersonationState: ImpersonationState = {
      isImpersonating: true,
      impersonatorEmail,
      targetEmail: impersonating || trimmedTargetEmail,
    };

    localStorage.setItem(
      IMPERSONATION_STATE_KEY,
      JSON.stringify(impersonationState)
    );

    // Re-identify so analytics is attributed to the impersonated user rather
    // than to whoever was identified at page load (CPF-1199).
    identifyCurrentUser();
  } catch (error) {
    localStorage.removeItem(ORIGINAL_TOKENS_KEY);
    throw new Error(getImpersonationErrorMessage(error, trimmedTargetEmail));
  }
}

/**
 * End impersonation session.
 * Restores original tokens and clears impersonation state.
 */
export function endImpersonation(): void {
  const originalTokensStr = localStorage.getItem(ORIGINAL_TOKENS_KEY);
  if (!originalTokensStr) {
    // No original tokens to restore, just clear impersonation state
    clearImpersonationState();
    return;
  }

  try {
    const originalTokens: OriginalTokens = JSON.parse(originalTokensStr);

    // Restore original tokens
    localStorage.setItem("access_token", originalTokens.access_token);
    localStorage.setItem("refresh_token", originalTokens.refresh_token);
    localStorage.setItem("expires_at", originalTokens.expires_at);
    localStorage.setItem("profile", originalTokens.profile);
    localStorage.setItem("claims", originalTokens.claims);
  } catch {
    // If restoration fails, clear everything and force re-login
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("expires_at");
    localStorage.removeItem("profile");
    localStorage.removeItem("claims");
  }

  clearImpersonationState();

  // Re-identify as the restored (impersonator) user (CPF-1199).
  identifyCurrentUser();
}
